import { Button, Checkbox, Select, Space } from 'antd'
import {
  ApprovalWorkflowDraft,
  SIGNOFF_ROLE_LABELS,
  SIGNOFF_ROLE_ORDER,
} from 'pages/reporting/shared/approval/approvalRules'
import { RoleRow } from 'pages/reporting/shared/approval/components/approvalRoleRow'
import { FC } from 'react'
import styled from 'styled-components'
import { ApprovalUserRef, SignoffRole } from 'types'

const QualificationsSelect = styled(Select<string>)`
  width: 16rem;
`

export interface AssigneeOption {
  value: string
  label: string
  ref: ApprovalUserRef
}

const setRoleRequired = (
  draft: ApprovalWorkflowDraft,
  role: SignoffRole,
  required: boolean
): ApprovalWorkflowDraft => ({
  ...draft,
  requiredRoles: required
    ? [...draft.requiredRoles, role]
    : draft.requiredRoles.filter((r) => r !== role),
})

const setRoleAssignee = (
  draft: ApprovalWorkflowDraft,
  role: SignoffRole,
  assignee: ApprovalUserRef | undefined
): ApprovalWorkflowDraft => ({
  ...draft,
  assignments: {
    ...draft.assignments,
    [role]: assignee && {
      ...assignee,
      qualifications: draft.assignments[role]?.qualifications,
    },
  },
})

const setRoleQualifications = (
  draft: ApprovalWorkflowDraft,
  role: SignoffRole,
  qualifications: string | undefined
): ApprovalWorkflowDraft => {
  const assignment = draft.assignments[role]
  if (!assignment) return draft
  return {
    ...draft,
    assignments: {
      ...draft.assignments,
      [role]: { ...assignment, qualifications },
    },
  }
}

interface ApprovalWorkflowEditorProps {
  draft: ApprovalWorkflowDraft
  assigneeOptions: AssigneeOption[]
  qualificationOptions: readonly string[]
  isSaving: boolean
  canCancel: boolean
  onChange: (draft: ApprovalWorkflowDraft) => void
  onSave: () => void
  onCancel: () => void
}

/** Pick which roles must approve the report and who fills each one. */
export const ApprovalWorkflowEditor: FC<ApprovalWorkflowEditorProps> = ({
  draft,
  assigneeOptions,
  qualificationOptions,
  isSaving,
  canCancel,
  onChange,
  onSave,
  onCancel,
}) => {
  const canSave =
    !isSaving &&
    draft.requiredRoles.length > 0 &&
    draft.requiredRoles.every((role) => draft.assignments[role])
  const qualificationSelectOptions = qualificationOptions.map((value) => ({
    value,
    label: value,
  }))

  return (
    <Space orientation='vertical' size='middle' style={{ width: '100%' }}>
      {SIGNOFF_ROLE_ORDER.map((role) => {
        const label = SIGNOFF_ROLE_LABELS[role]
        const required = draft.requiredRoles.includes(role)
        const assignment = draft.assignments[role]
        return (
          <RoleRow key={role}>
            <Checkbox
              checked={required}
              disabled={isSaving}
              onChange={(e) =>
                onChange(setRoleRequired(draft, role, e.target.checked))
              }>
              {label}
            </Checkbox>
            <Select<string, AssigneeOption>
              showSearch
              allowClear
              placeholder='Select team member'
              aria-label={`${label} assignee`}
              disabled={!required || isSaving}
              optionFilterProp='label'
              options={assigneeOptions}
              value={assignment?.userId}
              onChange={(_, option) =>
                onChange(
                  setRoleAssignee(
                    draft,
                    role,
                    Array.isArray(option) ? undefined : option?.ref
                  )
                )
              }
            />
            <QualificationsSelect
              allowClear
              placeholder='Qualifications'
              aria-label={`${label} qualifications`}
              disabled={!required || !assignment || isSaving}
              options={qualificationSelectOptions}
              value={assignment?.qualifications}
              onChange={(qualifications) =>
                onChange(setRoleQualifications(draft, role, qualifications))
              }
            />
          </RoleRow>
        )
      })}
      <Space>
        <Button
          type='primary'
          disabled={!canSave}
          loading={isSaving}
          onClick={onSave}>
          Save approval roles
        </Button>
        {canCancel && (
          <Button disabled={isSaving} onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Space>
    </Space>
  )
}
